import React, { PropsWithChildren, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FormGroup, ModalBody } from "reactstrap";
import { getIn } from "formik";

import { useCustomLanguageFormik } from "../../shared/Forms/DisplaySettingsForm/lib/useCustomLanguageForm";
import { useCustomLanguageClient } from "../../../api/grpc/workplaces/useCustomLanguageClient";

import { defaultLanguageObject } from "../../shared/Forms/DisplaySettingsForm/lib/defaultLanguageObject";
import { formatKeyName } from "../../shared/Forms/DisplaySettingsForm/lib/formatLanguageKeyName";
import { FormSection } from "../../shared/FormSection/FormSection";
import { Input } from "../../shared/Input/Input";
import { LabelWithInformationBox } from "../../shared/LabelWithInformationBox/LabelWithInformationBox";
import { InputDescription } from "../../shared/InputDescription/InputDescription";
import { ModalFooterContent } from "../ModalFooterContent/ModalFooterContent";
import { SUCCESS_STRINGS } from "../../../lib/utils/constants";

const CustomLanguageModalForm = ({
  toggleModal,
  langObj,
  langName,
  langId,
  refetch,
  refetchLanguages,
}: PropsWithChildren<{
  toggleModal: () => void;
  langObj?: Uint8Array;
  langName: string;
  langId: string;
  refetch: () => void;
  refetchLanguages: () => void;
}>) => {
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);

  const { updateCustomLanguage } = useCustomLanguageClient();

  let decoder = new TextDecoder();

  let decodedDataObject = decoder.decode(langObj);

  let parsedDataObject = JSON.parse(decodedDataObject);

  let definedLanguage = parsedDataObject.translation;

  const formik = useCustomLanguageFormik(
    {
      name: langName,
      ...definedLanguage,
    },
    {
      onSubmit: async (values) => {
        try {
          setMounted(true);
          setLoading(true);

          const { name, ...languageObject } = values;

          let jsonLanguageData = JSON.stringify({
            translation: languageObject,
          });

          let encoder = new TextEncoder();
          let languageDataAsUintArray = encoder.encode(jsonLanguageData);

          await updateCustomLanguage({
            id: langId,
            name: formik.values.name || "",
            languageData: languageDataAsUintArray,
          });

          refetch();

          refetchLanguages();
          toast.success(SUCCESS_STRINGS.customLanguageUpdated);
        } catch (error: any) {
          toast.error(
            "Custom language couldn't be updated, please reload and try again!"
          );
        } finally {
          setLoading(false);
          setMounted(true);
        }
      },
    }
  );

  useEffect(() => {
    return () => {
      setMounted(false);
    };
  }, []);

  const defineJsxElement = (key: string, value: any, parentKey?: string) => {
    const inputName = parentKey ? `${parentKey}.${key}` : key;

    //those keys should not show to the user and get transleted, so we hide them
    if (
      [
        "intro",
        "pairing",
        "settings",
        "network",
        "modalLobby",
        "modalDisconnect",
        "home.onScreenReservation",
        "flashMessage.unableToSyncMeetings",
        "toastMessage.noInternetConnection",
      ].includes(inputName)
    ) {
      return;
    }

    if (typeof value === "string") {
      const error = getIn(formik.errors, inputName);
      const touched = getIn(formik.touched, inputName);

      //Inputs should have the original English value from default langauge object as placeholders
      const defaultValue = getIn(defaultLanguageObject, inputName);

      return (
        <div className="pb-3" key={`${parentKey}-${key}`}>
          <LabelWithInformationBox for={inputName} title={formatKeyName(key)} />
          <Input
            id={inputName}
            {...formik.getFieldProps(inputName)}
            isTouched={true}
            errorMessage={error}
            placeholder={defaultValue}
            invalid={!!error && touched}
          />
        </div>
      );
    } else if (typeof value === "object") {
      return (
        <FormSection
          className={parentKey ? "pl-3" : "pl-0"}
          key={key}
          title={formatKeyName(key)}
          toggleable
        >
          {Object.entries(value)
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([childKey, childValue]) => {
              return defineJsxElement(childKey, childValue, inputName);
            })}
        </FormSection>
      );
    }
  };

  return (
    <>
      <ModalBody>
        <FormGroup className="CustomLanguageModal__name">
          <LabelWithInformationBox for="name" title="Name" />
          <Input
            id="name"
            type="text"
            name="name"
            value={formik.values.name}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            isTouched={formik.touched.name}
            errorMessage={formik.errors.name}
            invalid={!!formik.errors.name && formik.touched.name}
          />
        </FormGroup>
        <InputDescription
          title="Note!"
          description="
          Variables, ie words with {curly} brackets, must not be translated or modified - please leave as is."
        />
        {Object.entries(definedLanguage)
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([key, value]) => {
            return defineJsxElement(key, value);
          })}
      </ModalBody>

      <ModalFooterContent
        toggleModal={toggleModal}
        handleSave={() => formik.submitForm()}
        disabled={loading || !(formik.dirty && formik.isValid)}
        loadSpinner={loading}
        className="justify-content-center"
      />
    </>
  );
};

export default CustomLanguageModalForm;
